import { useIsCreateConversationMessageMutating } from "#features/conversations/hooks/useIsCreateConversationMessageMutating.ts";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

/**
 * Hook to handle automatic scrolling in a conversation.
 *
 * This hook observes the container's size changes and scrolls to the top of the last assistant message,
 * provided that a mutation is in progress and the user is not manually scrolling.
 *
 * @param scrollableContainerRef - Ref for the scrollable container.
 * @param conversationId - The id of the current conversation.
 * @param messagesCount - The total number of messages in the conversation.
 * @param hasPendingApproval - Whether the conversation waits on an approval prompt.
 * @returns An object with:
 *   - conversationContainerRef: Ref for the conversation container.
 *   - getAssistantMessageRef: Function to get the ref for the last assistant message.
 *   - pendingPromptRef: Ref the pending approval prompt puts its element in.
 */
export const useConversationAutoScroll = (scrollableContainerRef: RefObject<HTMLDivElement | null>, conversationId: string, messagesCount: number, hasPendingApproval: boolean) => {
    const isMutating = useIsCreateConversationMessageMutating(conversationId);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

    const conversationContainerRef = useRef<HTMLDivElement>(null);
    const lastAssistantMessageRef = useRef<HTMLDivElement>(null);
    const pendingPromptRef = useRef<HTMLDivElement>(null);
    const wasMutatingRef = useRef(isMutating);

    // Check for scroll-top query parameter on mount and scroll immediately
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("wai-scroll-top") === "true") {
            setShouldScrollToTop(true);
            // Scroll immediately to prevent jarring animation
            const container = scrollableContainerRef.current;
            if (container) {
                container.scrollTop = 0;
            }

            // Remove the query parameter from the URL
            urlParams.delete("wai-scroll-top");
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState(window.history.state, "", newUrl);
        }
    }, [scrollableContainerRef]);

    // Debounced function to scroll to the top of the last assistant message.
    const scrollToLastMessage = useDebouncedCallback(() => {
        const container = scrollableContainerRef.current!;

        // Don't auto-scroll if we're in scroll-to-top mode
        if (shouldScrollToTop) {
            return;
        }

        // Only scroll if the last assistant message ref is set and the user is not manually scrolling
        if (lastAssistantMessageRef.current && !isUserScrolling) {
            const lastAnswerOffsetTop = lastAssistantMessageRef.current.offsetTop;
            container.scrollTo({
                top: lastAnswerOffsetTop,
                behavior: "smooth"
            });
        }
    }, 200);

    // A turn that ends on an approval prompt waits on the user, so the prompt comes into view, and a scroll still queued, which
    // would pull the view off it, is dropped. An ordinary answer keeps its queued scroll: often its last, as the stream keeps
    // pushing it back. Only the conversation scrolls, never the page the assistant is embedded in, and opening a conversation
    // keeps its own scroll. Aiming at the prompt's top lands at the end when the prompt fits, so no "scroll to bottom" is left.
    useEffect(() => {
        const container = scrollableContainerRef.current;
        const prompt = pendingPromptRef.current;
        if (wasMutatingRef.current && !isMutating && hasPendingApproval) {
            scrollToLastMessage.cancel();
            if (container && prompt) {
                const reducesMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
                container.scrollTo({
                    top: container.scrollTop + prompt.getBoundingClientRect().top - container.getBoundingClientRect().top,
                    behavior: reducesMotion ? "auto" : "smooth"
                });
            }
        }
        wasMutatingRef.current = isMutating;
    }, [isMutating, hasPendingApproval, scrollToLastMessage, scrollableContainerRef]);

    /**
     * Effect to observe changes in the conversation container size.
     * Triggers scrollToLastMessage when the container resizes.
     */
    useEffect(() => {
        const container = conversationContainerRef.current;
        if (!container) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            // Scroll to the last message when the container resizes and a mutation is in progress
            if (isMutating) {
                scrollToLastMessage();
            }
        });

        resizeObserver.observe(container);

        // Listen to user interactions that indicate manual scrolling.
        const handleUserScroll = () => {
            setIsUserScrolling(true);
        };

        window.addEventListener("wheel", handleUserScroll, { passive: true });
        window.addEventListener("touchmove", handleUserScroll, { passive: true });
        window.addEventListener("click", handleUserScroll);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("wheel", handleUserScroll);
            window.removeEventListener("touchmove", handleUserScroll);
            window.removeEventListener("click", handleUserScroll);
        };
    }, [isMutating, scrollToLastMessage, setIsUserScrolling]);

    /**
     * Effect to reset the user scrolling state whenever a new message is added.
     * Also resets scroll-to-top mode to allow normal auto-scrolling after initial load.
     */
    useEffect(() => {
        setIsUserScrolling(false);
        setShouldScrollToTop(false);
    }, [messagesCount, setIsUserScrolling]);

    /**
     * Returns the ref it is the last message in the conversation.
     *
     * @param messageIndex - The index of the current message in the conversation.
     * @returns The ref for the last assistant message or null.
     */
    const getLastMessageRef = useCallback((messageIndex: number) => {
        return messageIndex === messagesCount - 1 ? lastAssistantMessageRef : null;
    }, [messagesCount]);

    return {
        conversationContainerRef,
        getLastMessageRef,
        pendingPromptRef
    };
};
